import { jsx, jsxs } from "react/jsx-runtime";
import {
  useRef,
  useState,
  useCallback,
  useMemo,
  createContext,
  useContext
} from "react";
import {
  DSDialog,
  DSDialogHeader,
  DSDialogBody,
  DSDialogFooter,
  DSDialogSeparator,
  DSDialogTitle,
  DSDialogAddon
} from "@elliemae/ds-dialog";
import { useFocusTrap } from "@elliemae/ds-utilities";
import { DSButtonV2 } from "@elliemae/ds-button";
import { Close } from "@elliemae/ds-icons";
const useDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerElement = useRef(null);
  const toggleDialog = useCallback(
    (event) => {
      setIsOpen((prev) => {
        if (!prev && event !== void 0 && event.target instanceof HTMLElement) {
          triggerElement.current = event?.target ?? null;
        }
        return !prev;
      });
    },
    [setIsOpen]
  );
  return { isOpen, toggleDialog, triggerElement };
};
const DialogContext = createContext({
  isOpen: false,
  toggleDialog: () => {
  },
  triggerElement: { current: null }
});
const DialogProvider = ({ children }) => {
  const { toggleDialog, isOpen, triggerElement } = useDialog();
  const value = useMemo(
    () => ({ toggleDialog, isOpen, triggerElement }),
    [toggleDialog, isOpen, triggerElement]
  );
  return /* @__PURE__ */ jsx(DialogContext.Provider, { value, children });
};
const useDialogContext = () => {
  const contextValue = useContext(DialogContext);
  if (!contextValue) {
    throw Error("useDialogContext needs to be used inside DialogProvider");
  }
  return contextValue;
};
const Dialog = ({
  isOpen,
  toggleDialog,
  onClickOutside = () => {
  },
  title,
  children,
  onApply,
  width,
  bodyHeight,
  centered,
  onClose,
  applyText = "Apply",
  cancelText = "Cancel",
  hideApply = false,
  hideCancel = false
}) => {
  const firstElementRef = useRef(null);
  const lastElementRef = useRef(null);
  const handleOnKeyDown = useFocusTrap({
    firstElementRef,
    lastElementRef
  });
  const handleClose = typeof onClose === "function" ? onClose : toggleDialog;
  return /* @__PURE__ */ jsxs(
    DSDialog,
    {
      isOpen,
      onClickOutside,
      onKeyDown: handleOnKeyDown,
      onClose: handleClose,
      width,
      centered,
      children: [
        /* @__PURE__ */ jsxs(DSDialogHeader, { children: [
          /* @__PURE__ */ jsx(DSDialogTitle, { children: title }),
          /* @__PURE__ */ jsx(DSDialogAddon, { children: /* @__PURE__ */ jsx(
            DSButtonV2,
            {
              buttonType: "icon",
              onClick: handleClose,
              "aria-label": "close dialog",
              innerRef: firstElementRef,
              "data-testid": "close-button-dialog",
              children: /* @__PURE__ */ jsx(Close, { color: ["brand-primary", "800"] })
            }
          ) })
        ] }),
        /* @__PURE__ */ jsx(DSDialogSeparator, {}),
        /* @__PURE__ */ jsx(
          DSDialogBody,
          {
            px: 24,
            mb: 16,
            pt: 2,
            tabIndex: 0,
            height: bodyHeight || "fit-content",
            children
          }
        ),
        /* @__PURE__ */ jsx(DSDialogSeparator, {}),
        /* @__PURE__ */ jsxs(DSDialogFooter, { children: [
          !hideCancel && /* @__PURE__ */ jsx(
            DSButtonV2,
            {
              buttonType: "outline",
              onClick: handleClose,
              "data-testid": "cancel-button-dialog",
              children: cancelText
            }
          ),
          !hideApply && /* @__PURE__ */ jsx(
            DSButtonV2,
            {
              disabled: typeof onApply !== "function",
              innerRef: lastElementRef,
              onClick: (e) => {
                if (typeof onApply === "function") {
                  onApply();
                }
              },
              "data-testid": "apply-button-dialog",
              children: applyText
            }
          )
        ] })
      ]
    }
  );
};
export {
  Dialog,
  useDialog
};
